import { Typography, Box, Flex, Button } from "@jewlr/storybook/core"
import { Link } from "react-router-dom"
import styled from "styled-components"

import { jewlr_t } from "helpers/application"

const DownloadBtn = styled(Button).attrs((props) => ({
  alignSelf: "center",
  forwardedAs: "a",
  mb: { _: 5, tablet: 0 },
  variant: "primary",
  ...props,
}))`
  font-size: 18px;
  font-weight: 500;
  height: 54px;
  letter-spacing: -0.02em;
  line-height: 22px;
  width: 286px;
`

const Container = styled(Box).attrs({
  contain: true,
  fontSize: "16px",
  fontWeight: "400",
  lineHeight: "1.24",
  mb: 7,
  mt: 4,
  mx: { _: 2, tablet: "auto" },
})`
  b {
    font-weight: 600;
  }

  a {
    text-decoration: underline;

    &:focus,
    &:hover {
      text-decoration: none;
    }
  }
`

const DecimalList = styled.ol`
  padding-left: 16px;

  li {
    margin-bottom: 16px;
    padding-left: 4px;
  }

  li::marker {
    font-weight: 600;
  }
`

const AlphaList = styled.ol`
  list-style-type: lower-alpha;
  padding-left: 16px;

  li {
    margin-bottom: 0;
  }
`

const TermsConditions = () => {
  return (
    <Container>
      <Flex
        flexDirection={{ _: "column-reverse", tablet: "row" }}
        justifyContent={{ _: "flex-start", tablet: "space-between" }}
        mb={{ _: "10px", tablet: 4 }}
      >
        <Typography
          as="h1"
          fontSize={{ _: "26px", tablet: "34px" }}
          fontWeight="400"
          letterSpacing="-0.02em"
          lineHeight="48px"
        >
          Retailers Terms and Conditions
        </Typography>
        <DownloadBtn
          href="https://assets.jewlr.com/portal/Retailer_Standard_Terms_and_Conditions.pdf"
          target="_blank"
        >
          Download PDF of Terms
        </DownloadBtn>
      </Flex>
      <Typography mb={2}>
        <b>Last Updated: [November 1, 2024]</b>
      </Typography>
      <DecimalList>
        <li>
          <Typography mb={2}>
            These Retailer Terms and Conditions (the <b>&ldquo;Terms&rdquo;</b>)
            outline your rights and obligations as the retailer, along with any
            applicable conditions, limitations and exclusions. Please read them
            carefully.
          </Typography>
          <Typography mb={2} ml="-20px">
            These Terms form an agreement between [Safyre Labs Inc.] (referred
            to as &quot;<b>us</b>&quot;, &quot;<b>we</b>&quot;, or &quot;
            <b>our</b>&quot; ) and the retailer (referred to as &ldquo;
            <b>you</b>&rdquo;) and govern your use of the Safyrelabs online
            purchasing platform, accessible at https://{sl.config.site_url} (the
            &quot;
            <b>Platform</b>&quot;). The Platform enables you to place orders
            (each an &ldquo;<b>Order</b>&rdquo;) for the purchase and
            {jewlr_t("terms.customization")} of a curated collection of{" "}
            {jewlr_t("terms.jewelry")} pieces (the &ldquo;
            <b>Products and Services</b>&rdquo;) for direct sale to your retail
            end-user consumers (the &ldquo;<b>End Customers</b>&rdquo;) in
            Canada.
          </Typography>
          <Typography mb={2} ml="-20px">
            We reserve the right to amend these Terms at any time by giving 30
            days&apos; notice. Any notices regarding modifications to these
            Terms, will be in a written form and provided to you: (i) by us via
            email (in each case to the address that you provide); or (ii) via
            the Platform. For notices made by email, the date of receipt will be
            deemed the date on which such notice is transmitted. You are not
            obliged to continue using the Platform after an amendment to these
            Terms is made; however, in the event you choose to not accept the
            changes, you must immediately cease use of the Platform and provide
            us with a written notice to that effect. Your continued use of the
            Platform to place Order following any amendment shall be deemed to
            be your acceptance of the amended Terms, and waiver of any
            additional notice requirements.
          </Typography>
        </li>
        <li>
          <b>Our Responsibilities.</b> We agree to:
          <AlphaList>
            <li>
              Provide you with access to the Platform, along with approved
              marketing materials, product images, and branding guidelines
              necessary to promote the Products and Services (the &ldquo;
              <b>Marketing Material</b>&rdquo;), as well as initial training on
              the use of the Platform and, as reasonably needed, ongoing support
              and assistance.
            </li>
            <li>
              Deliver the Products and Services in substantial compliance with
              the specifications displayed on our Platform.
            </li>
          </AlphaList>
        </li>
        <li>
          <b>Your responsibilities.</b> You agree to:
          <AlphaList>
            <li>
              In promoting the Products and Services, use exclusively the
              Marketing Materials provided by us, without alteration,
              modification.
            </li>
            <li>
              Provide after-sales support and services to End Customers,
              including addressing and fulfilling any requests, complaints our
              inquiries they may have.
            </li>
            <li>
              Not (i) share any discounts, including coupon code, provided to
              you as a retailer; (ii) misrepresent the Products and Services,
              engage in any unfair, anti-competitive, misleading, or deceptive
              practices regarding our Products and Services; and (iii) resell
              the Products and Services to anyone other than your direct end
              customers.
            </li>
          </AlphaList>
        </li>

        <li>
          <b>Order Acceptance and Cancellation.</b> You agree that your Order is
          an offer to purchase, under these Terms, all Products and Services
          listed in your Order. All Orders must be accepted by us, and we are
          not obligated to sell any Products or provide any Services to you
          until acceptance. We reserve the right to decline Orders, in whole or
          in part, at our sole discretion, even if we have sent you a
          confirmation email with your Order number and details of the items
          ordered. If your Order is not accepted, in full or in part, we will
          refund any amounts paid for the items and services that were not
          accepted.
        </li>

        <li>
          <b>Prices and Payment Terms.</b>
          <AlphaList>
            <li>
              All prices posted on the Platform or otherwise made available are
              subject to change, including due to market conditions or
              fluctuations in the raw material costs. In the event of any such
              change, we will provide you with no less than ten (10) business
              days&apos; advance notice via the Platform or by email (using the
              email you provided). The price charged for Products and Services
              will be the price displayed on the Platform at the time the Order
              is placed, subject to any applicable discounts, including those
              available to you through coupon codes. The price charged will be
              stated in your Order confirmation. Displayed prices do not include
              taxes or charges for shipping and handling. All such taxes and
              charges will be added to your total price and will be{" "}
              {jewlr_t("terms.itemized")} in your shopping{" "}
              {jewlr_t("terms.cart")} and in your Order confirmation. We strive
              to display accurate price information; however, we may, on
              occasion, make inadvertent typographical errors, inaccuracies, or
              omissions related to pricing and availability. We reserve the
              right to correct any errors, inaccuracies, or omissions at any
              time and to cancel any orders arising from such occurrences.
            </li>
            <li>
              You may not share or disclose any discounts, including coupon
              code, provided to you as a retailer.
            </li>
            <li>
              Payment in full must be received by us before our acceptance of an
              order. We accept all major {jewlr_t("terms.recognized")} credit
              cards for all purchases.
            </li>
          </AlphaList>
        </li>

        <li>
          <b>Shipments; Delivery; Title and Risk of Loss.</b>
          <AlphaList>
            <li>
              We will arrange for shipment of the Products to the shipping
              address you provided. You will pay all shipping and handling
              charges unless otherwise specified in the Order confirmation.
            </li>
            <li>
              Title and risk of loss pass to you upon our transfer of the
              Products to the shipment carrier. Shipping and delivery dates are
              estimates only and cannot be guaranteed. We are not liable for any
              delays in shipments.
            </li>
          </AlphaList>
        </li>
        <li>
          <b>Final Sales.</b> All sales are final, and returns or refunds are
          not accepted, except as provided under the limited warranty.
        </li>
        <li>
          <b>Limited Warranty.</b>
          <AlphaList>
            <li>
              We warrant to you that for a period of twelve (12) months from the
              date of shipment (&quot;<b>Warranty Period</b>&quot;), the
              Products and Services purchased through the Platform will be free
              from material defects in material and workmanship.
            </li>
            <li>
              <b>
                EXCEPT FOR THE WARRANTIES SET FORTH IN SECTION 8(a), WE MAKE NO
                WARRANTY OR CONDITION WHATSOEVER WITH RESPECT TO THE PRODUCTS OR
                SERVICES PURCHASED THROUGH THE PLATFORM, INCLUDING (i) ANY
                WARRANTY OR CONDITION OF MERCHANTABILITY, FITNESS FOR A
                PARTICULAR PURPOSE, OR TITLE; OR (ii) WARRANTY AGAINST
                INFRINGEMENT OF INTELLECTUAL PROPERTY RIGHTS OF A THIRD PARTY;
                WHETHER EXPRESS OR IMPLIED BY LAW, COURSE OF DEALING, COURSE OF
                PERFORMANCE, USAGE OF TRADE, OR OTHERWISE.
              </b>
            </li>
            <li>
              Products manufactured by a third party (&quot;
              <b>Third-Party Product</b>&quot;) may constitute, contain, be
              contained in, incorporated into, attached to, or packaged together
              with the Products. Third-Party Products are not covered by the
              warranty in Section 8(a). For the avoidance of doubt,{" "}
              <b>
                WE MAKE NO REPRESENTATIONS, WARRANTIES, OR CONDITIONS WITH
                RESPECT TO ANY THIRD-PARTY PRODUCT, INCLUDING ANY (i) WARRANTY
                OR CONDITION OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
                PURPOSE, OR TITLE; OR (ii) WARRANTY AGAINST INFRINGEMENT OF
                INTELLECTUAL PROPERTY RIGHTS OF A THIRD PARTY; WHETHER EXPRESS
                OR IMPLIED BY LAW, COURSE OF DEALING, COURSE OF PERFORMANCE,
                USAGE OF TRADE, OR OTHERWISE.
              </b>
            </li>
            <li>
              We shall not be liable for a breach of the warranty set forth in
              Section 8(a) if you or your End Customer alter or repair such
              Products without our prior written consent.
            </li>
            <li>
              Subject to Section 8(d), with respect to any such Products during
              the Warranty Period, we shall, in our sole discretion, either: (i)
              repair or replace such Products (or the defective part) or (ii)
              credit or refund the amounts paid by you for such Products
              provided that, if we so request, you shall, at your expense,
              return such Products to us.
            </li>
            <li>
              <b>
                THE REMEDIES SET FORTH IN SECTION 8(e) SHALL BE YOUR SOLE AND
                EXCLUSIVE REMEDY AND OUR ENTIRE LIABILITY FOR ANY BREACH OF THE
                LIMITED WARRANTIES SET FORTH IN SECTION 8(a).
              </b>
            </li>
          </AlphaList>
        </li>
        <li>
          <b>Limitation of Liability and indemnification.</b>
          <AlphaList>
            <li>
              <b>
                IN NO EVENT SHALL WE BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY
                LOSS OF USE, REVENUE, OR PROFIT, OR FOR ANY CONSEQUENTIAL,
                INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE, OR
                AGGRAVATED DAMAGES WHETHER ARISING OUT OF BREACH OF CONTRACT,
                TORT (INCLUDING NEGLIGENCE) OR OTHERWISE, REGARDLESS OF WHETHER
                SUCH DAMAGES WERE FORESEEABLE AND WHETHER OR NOT WE HAVE BEEN
                ADVISED OF THE POSSIBILITY OF SUCH DAMAGES, AND NOTWITHSTANDING
                THE FAILURE OF ANY AGREED OR OTHER REMEDY OF ITS ESSENTIAL
                PURPOSE.
              </b>
            </li>
            <li>
              <b>
                IN NO EVENT SHALL OUR AGGREGATE LIABILITY ARISING OUT OF OR
                RELATED TO THESE TERMS, WHETHER ARISING OUT OF OR RELATED TO
                BREACH OF CONTRACT, TORT (INCLUDING NEGLIGENCE), OR OTHERWISE,
                EXCEED THE AMOUNTS PAID BY YOU FOR THE PRODUCTS AND SERVICES
                SOLD THROUGH THIS PLATFORM.
              </b>
            </li>
            <li>
              You agree to indemnify, defend, and hold us harmless from and
              against any and all claims, liabilities, damages, losses,
              expenses, and costs (including reasonable attorneys&apos; fees)
              arising from or related to (i) your failure to comply these Terms;
              (ii) any misuse or un{jewlr_t("terms.authorize")}d use of the
              Platform, the Marketing Material and/or our intellectual property
              rights; and (iii) any violations of applicable laws or
              regulations.
            </li>
          </AlphaList>
        </li>

        <li>
          <b>Intellectual Property Use and Ownership.</b> You acknowledge and
          agree that:
          <AlphaList>
            <li>
              We, and where applicable our licensors, are and will remain the
              sole and exclusive owners of all intellectual property rights in
              and to each Products and Services made available on this Platform
              and any related product designs, specifications, logs
              instructions, documentation, or other materials (including
              Marketing Material), including, but not limited to, all related
              copyrights, patents, and trademarks and other intellectual
              property rights.
            </li>
            <li>
              You are granted a non-exclusive, non-transferable,
              non-sublicensable right to use the Marketing Material and
              trademarks solely for the promotion and sale of the Products and
              Services, in accordance with these Terms. Except as expressly
              licensed herein, no other rights to any intellectual property are
              granted. You shall notify us of any actual or suspected
              infringement of our intellectual property rights of which you
              become aware and, upon request, reasonably assist us in related
              actions or proceedings at our expense. Upon termination or
              expiration of these Terms, all rights under this Section
              immediately cease, and you shall promptly return or permanently
              destroy the Marketing Material and discontinue all use of our
              trademarks.
            </li>
          </AlphaList>
        </li>

        <li>
          <b>Termination.</b>
          <AlphaList>
            <li>
              Either party may terminate these Terms by providing thirty (30)
              days&apos; advance written notice to the other party.
            </li>
            <li>
              We may immediately terminate these Terms and/or remove or suspend
              your access to the Platform if you fail to comply with any of
              these Terms, including the Guidelines.
            </li>
          </AlphaList>
        </li>
        <li>
          <b>Confidentiality.</b> You agree to maintain the confidentiality of
          all non-public, proprietary, or confidential information obtained from
          us, including but not limited to details about the Platform,
          discounts, Product designs, pricing, and these Terms. Such information
          may only be used for purposes permitted under these Terms and
          disclosed solely to employees or agents who need to know for those
          purposes, provided they are bound by confidentiality obligations. This
          obligation shall survive the termination or expiration of these Terms.
        </li>
        <li>
          <b>Privacy.</b> We respect your privacy and are committed to
          protecting it. Our{" "}
          <Link target="_blank" to="/privacy-policy">
            Platform Privacy Policy
          </Link>
          ,{" "}
          <Link target="_blank" to="/privacy-policy">
            https://
            {sl.config.site_url}/privacy-policy
          </Link>
          , governs the processing of all personal information collected from
          you in connection with your purchase of products or services through
          the Platform.
        </li>
        <li>
          <b>Force Majeure.</b> Neither party shall be liable for delay or
          failure of performance of any obligations required by these Terms when
          such delay or failure arises from circumstances beyond the reasonable
          control of such party. Such causes may include, without limitation,
          acts of God, acts of government in its sovereign or contractual
          capacity, acts of a public enemy, acts of civil or military authority,
          war, riots, civil strife, terrorism, blockades, sabotage, rationing,
          embargoes, epidemics, pandemics, earthquakes, fire, flood, quarantine
          restrictions, power shortages or failures, utility or communication
          failures or delays, labor disputes, strikes, or shortages, supply
          shortages, equipment failures or solitary malfunctions. The time for
          performance of any act delayed by such events may be postponed for a
          period equal to the delay.
        </li>
        <li>
          <b>Governing Law and Forum.</b> All matters arising out of or relating
          to these Terms are governed by and construed in accordance with the
          laws of the Province of Ontario and the federal laws of Canada
          applicable therein without giving effect to any choice or conflict of
          law provision or rule (whether of the Province of Ontario or any other
          jurisdiction). The parties hereto irrecoverably attorn to the
          exclusive jurisdiction of the courts of the Province of Ontario, and
          any suit or claim hereunder will be brought solely in the courts
          located in the City of Toronto.
        </li>
        <li>
          <b>Assignment.</b> You may not assign these Terms, nor any of your
          rights or obligations under them, without our prior written consent.
        </li>
        <li>
          <b>No Waivers.</b> The failure or delay by us to exercise or enforce
          any right or provision of these Terms will not constitute a waiver of
          future enforcement of that right or provision. The waiver of any right
          or provision will be effective only if in writing and signed by a duly
          by our {jewlr_t("terms.authorize")}d representative.
        </li>
        <li>
          <b>No Third-Party Beneficiaries.</b> These Terms do not and are not
          intended to confer any rights or remedies upon any person or entity
          other than you.
        </li>
        <li>
          <b>Notices.</b>
          <AlphaList>
            <li>
              To You. We may provide any notice to you under these Terms by: (i)
              sending a message to the email address you provide; or (ii)
              posting to the Platform. Notices sent by email will be effective
              when we send the email, and notices we provide by posting will be
              effective upon posting. It is your responsibility to keep your
              email address current.
            </li>
            <li>
              To Us. To give us notice under these Terms, you must contact us by
              email to wholesale@safyrelabs.com. We may update our email address
              for notices to us by posting a notice on the Platform. Notices
              provided by email mail will be effective when received by us.
            </li>
          </AlphaList>
        </li>
        <li>
          <b>Severability.</b> If any provision of these Terms is invalid,
          illegal, void, or unenforceable, that provision will be deemed severed
          from these Terms and will not affect the validity or enforceability of
          the remaining provisions of these Terms.
        </li>
        <li>
          <b>Entire Agreement.</b> Our Order confirmation, these Terms and our
          Privacy Policy will be deemed the final and integrated agreement
          between you and us on the matters contained in these Terms.
        </li>
      </DecimalList>
    </Container>
  )
}

export default TermsConditions
